import { identity } from '@kofno/piper';

import {
  EditLearningCollectionResource,
  LearningCollectionResource,
  LearningCollectionResourceWithScheduledPrograms,
} from './Types';
import { SourceIdentifier } from '../../ChatbotPreview/Types';
import { LogoChange } from './LearningCollectionContext';
import { assertNever } from '../../../../../Decoders';

type DisplayType = 'carousel' | 'list' | 'grid';

type LearningDesignState = 'custom' | 'curated' | 'blank' | 'build-your-own' | 'browse-all';

interface LogoOut {
  obj_name: string;
  asset_type: string;
  file_name: string;
  extension: string;
}

interface ProductCollectionOut {
  id: number | undefined;
  name: string;
  description: string;
  experiences: number[];
  display_per_page_count: number;
  display_type: DisplayType;
  browse_all: boolean;
  learning_design_state: LearningDesignState;
  display_order: number;
  display_button_label: string | undefined;
}

interface ScheduledProgramInstanceOut {
  id: number;
  enrollable: boolean;
}

interface ScheduledProgramOut {
  id: number; // program_family_id (experience ID)
  scheduled_program_instances: ScheduledProgramInstanceOut[]; // program instances with enrollability
}

interface LearningCollectionOut {
  learning_collection: {
    id: number | undefined;
    name: string;
    internal_name: string | undefined;
    description: string;
    search: boolean;
    client_identifier: string;
    published: boolean;
    learning_designs: ProductCollectionOut[];
    source_identifier: SourceIdentifier;
    logo?: LogoOut;
    scheduled_programs?: ScheduledProgramOut[];
  };
}

export const learningCollectionEncoder = (
  resource:
    | LearningCollectionResource
    | EditLearningCollectionResource
    | LearningCollectionResourceWithScheduledPrograms,
  logoChange: LogoChange,
): LearningCollectionOut => {
  const result: LearningCollectionOut = {
    learning_collection: {
      id: resource.payload.id.map<number | undefined>(identity).getOrElseValue(undefined),
      name: resource.payload.name,
      internal_name: resource.payload.internal_name
        .map<string | undefined>(identity)
        .getOrElseValue(undefined),
      description: resource.payload.description,
      client_identifier: resource.payload.client_identifier,
      search: resource.payload.search,
      published: resource.payload.published_at.isJust(),
      source_identifier: 'builder-v2',
      learning_designs: resource.payload.product_collections.map((productCollection, index) => ({
        id: productCollection.payload.id
          .map<number | undefined>(identity)
          .getOrElseValue(undefined),
        name: productCollection.payload.name,
        description: productCollection.payload.description,
        experiences: productCollection.payload.experiences,
        display_per_page_count: productCollection.payload.display_per_page_count,
        display_type: productCollection.payload.display_type,
        browse_all: productCollection.payload.browse_all,
        learning_design_state: productCollection.payload.learning_design_state,
        display_order: index,
        display_button_label: productCollection.payload.display_button_label
          .map<string | undefined>(identity)
          .getOrElseValue(undefined),
      })),
    },
  };

  // The logo field is only sent when the admin changed the logo, so an edit
  // that leaves it alone keeps the existing one. A removal is a logo with
  // blank fields, the same message Builder v1 sends; the persistence service
  // deletes the asset when it sees no file name (DVLP-7971).
  switch (logoChange.kind) {
    case 'none':
      break;
    case 'upload':
      result.learning_collection.logo = {
        obj_name: logoChange.data.objName,
        asset_type: logoChange.data.assetType,
        file_name: logoChange.data.fileName,
        extension: logoChange.data.extension,
      };
      break;
    case 'remove':
      result.learning_collection.logo = {
        obj_name: '',
        asset_type: '',
        file_name: '',
        extension: '',
      };
      break;
    default:
      assertNever(logoChange);
  }

  // Convert scheduled_programs from resourceState to API format
  // Include ALL entries (even with empty program_ids) so backend knows which families to delete from
  const scheduledPrograms = (
    resource.payload as LearningCollectionResourceWithScheduledPrograms['payload']
  ).scheduled_programs;

  const scheduledProgramsOut = (scheduledPrograms ?? []).map((sp) => ({
    id: sp.id,
    scheduled_program_instances: sp.program_ids.map((programId) => ({
      id: programId,
      enrollable: true,
    })),
  }));

  return {
    learning_collection: {
      ...result.learning_collection,
      scheduled_programs: scheduledProgramsOut,
    },
  };
};
